// WASD + E/Q camera movement for the 3D perspective views (model 3D + floor 3D).
//
// Held-key locomotion: while a movement key is down, update() slides the orbit
// camera AND its target each frame (so the orbit pivot rides along and
// rotate/zoom keep working); W/S fly along the full look direction (following
// pitch), A/D strafe horizontally, and E/Q rise/descend along world Y. The
// keys are rebindable hotkey actions read live, so the Settings overlay
// customises them like any other shortcut. Speed scales with the current view
// distance — same feel zoomed in or out — and is framerate-independent (real dt).
//
// Self-contained: owns its own held-key tracking (keydown/keyup/blur) and a
// per-frame mover. main installs it once and calls update() each desktop frame.

import * as THREE from "three";
import type { AppContext } from "./context";
import { keyForAction } from "../ui/hotkeys";

const VIEW_FRACTION_PER_SEC = 1.0; // holding ~1s traverses one view-distance

export function installCameraMove(app: AppContext): void {
  const { camera, orbitControls, activeCamera, wakeRendering, isPresenting } = app;

  const heldKeys = new Set<string>();
  const _fwd = new THREE.Vector3();
  const _right = new THREE.Vector3();
  const _delta = new THREE.Vector3();
  const _up = new THREE.Vector3(0, 1, 0);
  let _lastMs = 0;

  const held = (id: string): boolean => {
    const k = keyForAction(id);
    return k !== "" && heldKeys.has(k);
  };

  function update(): void {
    const now = performance.now();
    const dt = _lastMs ? Math.min(0.05, (now - _lastMs) / 1000) : 0;
    _lastMs = now;
    // Only the two 3D perspective orbit views (model 3D + floor 3D) use `camera`;
    // 2D plan / ortho use other cameras, where ground-plane WASD makes no sense.
    if (activeCamera() !== camera) return;
    const el = document.activeElement;
    if (el instanceof HTMLInputElement || el instanceof HTMLTextAreaElement) return;
    const fb = (held("move-forward") ? 1 : 0) - (held("move-back") ? 1 : 0);
    const lr = (held("move-right") ? 1 : 0) - (held("move-left") ? 1 : 0);
    const ud = (held("move-up") ? 1 : 0) - (held("move-down") ? 1 : 0); // world up/down
    if ((fb === 0 && lr === 0 && ud === 0) || dt === 0) return;
    // Forward = the full direction the camera looks (follows pitch, so looking
    // down + W flies toward the ground); right is the horizontal strafe ⟂ to the
    // look direction; up/down is straight along world Y regardless of look.
    camera.getWorldDirection(_fwd).normalize();
    _right.crossVectors(_fwd, _up);
    if (_right.lengthSq() < 1e-6) _right.crossVectors(camera.up, _up); // looking ~straight down
    _right.normalize();
    _delta
      .set(0, 0, 0)
      .addScaledVector(_fwd, fb)
      .addScaledVector(_right, lr)
      .addScaledVector(_up, ud);
    if (_delta.lengthSq() < 1e-9) return;
    _delta.normalize(); // a diagonal isn't faster than a cardinal
    const dist = Math.max(0.01, camera.position.distanceTo(orbitControls.target));
    _delta.multiplyScalar(dist * VIEW_FRACTION_PER_SEC * dt);
    camera.position.add(_delta);
    orbitControls.target.add(_delta);
    wakeRendering();
  }

  // Track bare (un-modified) held keys for the mover. Mirrors main's keydown
  // guards: not in VR, not while typing, and no Ctrl/Cmd/Alt (those are shortcuts,
  // not movement). keyup clears the key; blur clears all, since a dropped focus
  // (alt-tab, devtools, a DOM input) can swallow the keyup and leave a key stuck.
  window.addEventListener("keydown", (e) => {
    if (isPresenting()) return;
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) {
      return;
    }
    if (!e.ctrlKey && !e.metaKey && !e.altKey) heldKeys.add(e.key.toLowerCase());
  });
  window.addEventListener("keyup", (e) => heldKeys.delete(e.key.toLowerCase()));
  window.addEventListener("blur", () => heldKeys.clear());

  // Run in the non-VR pre-branch slot; the mover self-gates to the 3D views.
  app.frameLoop.register("desktopPre", update);
}
